import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { formatDateTime } from "../lib/format";

const ROLES = [
  { value: "admin_tech", label: "Admin tecnico" },
  { value: "admin_finance_hr", label: "Admin amministrazione/HR" },
  { value: "collaborator_generic", label: "Collaboratore" },
];

export default function Collaborators() {
  const [users, setUsers] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [stepupPwd, setStepupPwd] = useState("");
  const [inv, setInv] = useState({ email: "", first_name: "", last_name: "", role: "collaborator_generic" });

  const load = async () => {
    try { setUsers(await api.listUsers()); } catch {}
    try { setInvitations(await api.invitations()); } catch {}
  };
  useEffect(() => { load(); }, []);

  const doStepup = async () => {
    try {
      const res = await api.stepupPassword(stepupPwd);
      window.__pxos_stepup = res.stepup_token;
      return true;
    } catch { toast.error("Password non corretta"); return false; }
  };

  const invite = async () => {
    if (!await doStepup()) return;
    try {
      await api.createInvitation(inv);
      toast.success("Invito inviato");
      setInv({ email: "", first_name: "", last_name: "", role: "collaborator_generic" });
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };

  const suspend = async (id) => {
    if (!await doStepup()) return;
    try { await api.suspendUser(id); toast.success("Utente sospeso"); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };
  const unsuspend = async (id) => {
    if (!await doStepup()) return;
    try { await api.unsuspendUser(id); await load(); } catch { toast.error("Errore"); }
  };
  const revokeInvite = async (id) => { await api.revokeInvitation(id); load(); };

  return (
    <div className="space-y-8" data-testid="collaborators-page">
      <div>
        <h2 className="font-display text-xl font-semibold">Collaboratori</h2>
        <p className="text-sm opacity-70">Invita collaboratori e gestisci i loro accessi. Ogni azione richiede la tua password (step-up).</p>
      </div>

      <section className="pxos-card p-6" data-testid="invite-form-section">
        <h2 className="font-display text-lg font-semibold">Nuovo invito</h2>
        <div className="mt-3 grid sm:grid-cols-2 gap-3">
          <label><span className="text-xs uppercase tracking-widest">Nome</span>
            <input value={inv.first_name} onChange={(e) => setInv({...inv, first_name: e.target.value})} className="pxos-input" data-testid="invite-first" /></label>
          <label><span className="text-xs uppercase tracking-widest">Cognome</span>
            <input value={inv.last_name} onChange={(e) => setInv({...inv, last_name: e.target.value})} className="pxos-input" data-testid="invite-last" /></label>
          <label className="sm:col-span-2"><span className="text-xs uppercase tracking-widest">Email</span>
            <input type="email" value={inv.email} onChange={(e) => setInv({...inv, email: e.target.value})} className="pxos-input" data-testid="invite-email" /></label>
          <label><span className="text-xs uppercase tracking-widest">Ruolo</span>
            <select value={inv.role} onChange={(e) => setInv({...inv, role: e.target.value})} className="pxos-input" data-testid="invite-role">
              {ROLES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
            </select></label>
          <label><span className="text-xs uppercase tracking-widest">La tua password (step-up)</span>
            <input type="password" value={stepupPwd} onChange={(e) => setStepupPwd(e.target.value)} className="pxos-input" data-testid="invite-stepup" /></label>
        </div>
        <button className="pxos-btn-primary mt-4" onClick={invite} data-testid="invite-submit">Invia invito</button>
      </section>

      <section className="pxos-card p-6" data-testid="users-section">
        <h2 className="font-display text-lg font-semibold">Utenti attivi</h2>
        <ul className="divide-y mt-3" data-testid="users-list">
          {users.map((u) => (
            <li key={u.id} className="py-3 flex items-center justify-between" data-testid="user-row">
              <div>
                <div className="font-medium">{u.first_name} {u.last_name} {u.is_owner ? <span className="pxos-badge ml-1">Owner</span> : null}</div>
                <div className="text-xs opacity-70">{u.email} · {u.roles?.filter(Boolean).join(", ") || "—"}</div>
              </div>
              <div className="flex items-center gap-2">
                <span className="pxos-badge" data-testid="user-status">{u.suspended_at ? "Sospeso" : (u.is_active ? "Attivo" : "Non attivo")}</span>
                {!u.is_owner && (u.suspended_at ?
                   <button className="text-xs underline" onClick={() => unsuspend(u.id)} data-testid="user-unsuspend-btn">Riattiva</button> :
                   <button className="text-xs underline" onClick={() => suspend(u.id)} data-testid="user-suspend-btn">Sospendi</button>)}
              </div>
            </li>
          ))}
        </ul>
      </section>

      <section className="pxos-card p-6" data-testid="invitations-section">
        <h2 className="font-display text-lg font-semibold">Inviti</h2>
        <ul className="divide-y mt-3" data-testid="invitations-list">
          {invitations.map((i) => (
            <li key={i.id} className="py-3 flex items-center justify-between" data-testid="invitation-row">
              <div>
                <div className="font-medium">{i.first_name} {i.last_name} — {i.email}</div>
                <div className="text-xs opacity-70">{i.role} · Scade {formatDateTime(i.expires_at)}</div>
              </div>
              <div className="flex items-center gap-2">
                <span className="pxos-badge" data-testid="invitation-status">{i.status}</span>
                {i.status === "pending" && <button className="text-xs underline" onClick={() => revokeInvite(i.id)} data-testid="invitation-revoke-btn">Revoca</button>}
              </div>
            </li>
          ))}
          {invitations.length === 0 && <li className="py-3 text-sm opacity-70">Nessun invito.</li>}
        </ul>
      </section>
    </div>
  );
}
